import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What\'s the difference between a preferred share and a common share?',
    a: 'Preferred shares sit between common stock and bonds in the capital structure. They typically pay a FIXED dividend (often quarterly), have a stated par value (usually $25 or $50), have NO voting rights, and rank ahead of common shares for both dividend payments and liquidation proceeds. Their prices move much less than common stock — preferreds are more interest-rate sensitive than business-result sensitive.',
  },
  {
    q: 'What\'s call risk on preferreds?',
    a: 'Most preferreds are CALLABLE — the company can buy them back at par ($25 or $50) on or after a specific date, usually 5 years from issuance. If rates fall and the company can refinance cheaper, they\'ll call your preferred at par and you lose any premium you paid. Practical rule: never pay more than ~5-10% above par for a callable preferred near its call date.',
  },
  {
    q: 'Are preferred dividends qualified?',
    a: 'It depends. Most common-stock-style preferreds ("non-cumulative perpetual preferreds" issued by banks like JPM, BAC, WFC) pay QUALIFIED dividends taxed at 0/15/20% rates. Some pass-through structures (REIT preferreds, trust preferreds) pay NON-qualified dividends taxed as ordinary income. Always check the prospectus or 1099-DIV box 1b vs box 1a.',
  },
  {
    q: 'Why use a preferred ETF instead of picking individual preferreds?',
    a: 'Individual preferreds are illiquid (wide bid-ask spreads) and require homework on each issuer\'s credit, call schedule, and tax treatment. Preferred ETFs like PFF, PFFD, PSK, and PFFA bundle hundreds of issues, handle the reinvestment, and trade with normal liquidity. The expense ratio (0.4-0.8%) is worth it for most investors.',
  },
];

export default function LearnPreferredStocks() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="Preferred Stocks vs Common Stocks — When 6-10% Preferreds Beat Common | DiviDrip"
      description="The 'third asset class' most dividend investors ignore. PFF, PFFA, PFFD, PSK — the call-risk gotcha, the qualified-vs-ordinary tax wrinkle, and why retirees with a 7-10 year horizon love them."
      canonical="https://twylightcrow.com/learn/preferred-stocks-explained"
      guidePath="/learn/preferred-stocks-explained"
      sectionLabel="Vehicles & Structures"
      sectionAnchor="vehicles"
      kicker="Learn · Income vehicles"
      h1="Preferred Stocks vs Common Stocks — When 6-10% Preferreds Beat Common"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/covered-call-etfs', label: 'Covered-call ETFs — JEPI, JEPQ, QYLD' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs' },
        { to: '/learn/is-9-percent-yield-safe', label: 'Is a 9%+ yield safe?' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends' },
        { to: '/learn/bond-yields-vs-dividend-yields', label: 'Bond yields vs dividend yields' },
      ]}
    >
      <p>
        Most dividend investors know common stocks and bonds. Between them sits
        a third asset class — <strong>preferred shares</strong> — that yields
        6-10%, behaves more like a bond than a stock, and gets almost no
        coverage in beginner content. Let&apos;s fix that.
      </p>

      <h2>Where preferreds sit in the capital stack</h2>
      <table className="learn-table">
        <thead>
          <tr><th>Rank</th><th>Instrument</th><th>Typical yield</th><th>Risk profile</th></tr>
        </thead>
        <tbody>
          <tr><td>1 (highest)</td><td>Senior secured debt</td><td>5-7%</td><td>Lowest risk; first to be paid in bankruptcy</td></tr>
          <tr><td>2</td><td>Unsecured bonds</td><td>5-8%</td><td>Higher risk than secured debt</td></tr>
          <tr><td>3</td><td><strong>Preferred shares</strong></td><td><strong>6-10%</strong></td><td>Below bonds, above common; fixed dividend</td></tr>
          <tr><td>4 (lowest)</td><td>Common shares</td><td>0-6%</td><td>Last in line; variable dividend; full equity upside</td></tr>
        </tbody>
      </table>
      <p>
        Preferreds are senior to common stock — preferred dividends MUST be paid
        before any common dividend is paid. If the company stops paying, common
        holders get nothing. But preferreds rank BELOW bondholders, so they
        carry slightly more credit risk than bonds.
      </p>

      <h2>The 4 main preferred ETFs</h2>
      <p>Live yields from the DiviDrip database:</p>
      <table className="learn-table">
        <thead>
          <tr><th>Ticker</th><th>Yield</th><th>Expense</th><th>What\'s inside</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>PFF</strong></td>
            <td>~6.0%</td>
            <td>0.46%</td>
            <td>iShares Preferred and Income Securities — 400+ US preferreds, mostly bank issuers, $14B+ AUM</td>
          </tr>
          <tr>
            <td><strong>PFFD</strong></td>
            <td>~6.5%</td>
            <td>0.23%</td>
            <td>Global X US Preferred — similar mix to PFF but cheaper expense ratio</td>
          </tr>
          <tr>
            <td><strong>PSK</strong></td>
            <td>~7.0%</td>
            <td>0.10%</td>
            <td>State Street SPDR ICE Preferred Securities — broadest mandate, lowest cost</td>
          </tr>
          <tr>
            <td><strong>PFFA</strong></td>
            <td>~10.1%</td>
            <td>1.55%</td>
            <td>Virtus InfraCap US Preferred — actively managed, ~30% leverage, higher risk and reward</td>
          </tr>
        </tbody>
      </table>
      <p>
        PFFA stands out — it&apos;s actively managed AND leveraged, which is
        what gets you the 10%+ yield. Higher fees and higher volatility, but the
        income is genuine.
      </p>

      <h2>The call-risk gotcha (the #1 thing beginners miss)</h2>
      <p>
        Most preferreds are <strong>callable</strong> — the issuing company can
        redeem them at par (usually $25 or $50) on or after a specific date,
        typically 5 years from issuance. If you bought a $25-par preferred for
        $27 because rates fell after issuance, and the company calls at $25,
        you LOSE the $2 premium.
      </p>
      <p>The rules to live by:</p>
      <ul>
        <li>Never pay more than ~5-10% above par for a callable preferred.</li>
        <li>For preferreds trading above par, check the call date and yield-to-call (YTC), not just current yield.</li>
        <li>If the call date is far away and rates are falling, that&apos;s when call risk activates — issuers refinance cheaper.</li>
      </ul>
      <p>
        ETFs like PFF and PFFD reinvest the proceeds when an issue gets called,
        so the call risk is smoothed across the portfolio. That&apos;s a real
        argument for the ETF route.
      </p>

      <h2>Tax treatment — the qualified vs ordinary wrinkle</h2>
      <p>
        This catches retirees off guard. Most bank-issued preferreds pay{' '}
        <strong>qualified</strong> dividends (taxed at 0/15/20% federal). But
        some pass-through structures pay <strong>ordinary income</strong>:
      </p>
      <ul>
        <li><strong>Qualified</strong>: JPM-PR-D, BAC-PR-Q, most C-corp issued preferreds.</li>
        <li><strong>Ordinary</strong>: REIT preferreds (O.PR-A, KIM.PR-L), trust preferreds, MLP-related preferreds.</li>
      </ul>
      <p>
        Inside PFF and PFFD, the mix is mostly qualified (heavy bank weight) so
        your 1099-DIV will largely show qualified dividends. PFFA is more mixed
        because of its actively-managed REIT/MLP allocations. See{' '}
        <Link to="/learn/qualified-vs-ordinary-dividends">qualified vs
        ordinary</Link> for the full tax math.
      </p>

      <h2>When preferreds make sense for YOU</h2>
      <p>The strongest use cases:</p>
      <ol>
        <li>
          <strong>Retiree in late accumulation or early withdrawal.</strong> You
          want 6-8% yield that&apos;s steadier than stocks but better than bonds.
          A 5-10% allocation to PFF or PFFD bridges that gap.
        </li>
        <li>
          <strong>You believe rates have peaked.</strong> Preferreds are
          long-duration like bonds — when rates fall, prices rise. The fixed
          dividend looks better and better in a falling-rate world.
        </li>
        <li>
          <strong>You want bank exposure without bank equity volatility.</strong>{' '}
          Bank preferreds let you collect 6-8% from JPM, BAC, WFC without
          riding their common-share price swings.
        </li>
      </ol>

      <p>Bad use cases:</p>
      <ul>
        <li>You&apos;re young, accumulating, and want growth — common stock outperforms preferreds over 20+ years.</li>
        <li>You think rates are about to rise sharply — preferreds will fall like long bonds.</li>
        <li>You don&apos;t understand call risk and you&apos;re buying individual issues at 10%+ above par.</li>
      </ul>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Bottom line</h2>
      <p>
        Preferreds aren&apos;t for everyone, but they&apos;re an underrated tool
        for retirees and late-accumulators who want bond-like steadiness with
        stock-like yields. Start with PFF or PFFD for diversification + low
        cost. Add PFFA if you can stomach the leverage for the extra 3-4%
        yield. Skip individual preferreds until you understand call risk
        cold.
      </p>
    </LearnPage>
  );
}
